import React from "react";
import { Card } from "@UI/Components/Card/Card";
import { Box } from "@UI/Components/Box/Box";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useThemeToken from "@UI/hooks/useThemeToken";
import { WhiteSuperTalentBadge } from "@UI/Components/Icons/WhiteSuperTalent";
import { Icon } from "@UI/Components/Icon/Icon";
import { Heading } from "@UI/Components/Heading/Heading";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useFeatureFlag } from "@Libs/FeatureFlags/hooks/useFeatureFlag";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useServiceProviderMetrics } from "@modules/ServiceProviders/hooks/useServiceProviderMetrics";
import {
  IExtendedMetric,
  ITrackingTypes,
} from "@modules/ServiceProviders/types/serviceProvider.types";
import { LoyalClienteleCard } from "../SuperTalentMetrics/MetricCards/LoyalClienteleCard";
import { PromiseKeeperCard } from "../SuperTalentMetrics/MetricCards/PromiseKeeperCard";
import { TopPickCard } from "../SuperTalentMetrics/MetricCards/TopPickCard";
import messages from "./messages";
import { TrackingCard } from "../SuperTalent/TrackingCard";

type TGoalContext =
  | "repeat_rate"
  | "realisation_rate"
  | "realisation_rate_blocked"
  | "selection_rate";

interface IProps {
  context: TGoalContext;
}

export const SuperTalentGoalCard = ({ context }: IProps) => {
  const navigate = useNavigate();
  const { isEnabled: isNewMetricsEnabled } = useFeatureFlag(
    "additional_supertalent_metrics"
  );

  const pink500 = useThemeToken("colors", "pink.500");
  const orange500 = useThemeToken("colors", "orange.500");
  const pink600 = useThemeToken("colors", "pink.600");
  const orange600 = useThemeToken("colors", "orange.600");

  const { currentUser } = useCurrentUser({ includes: ["serviceProvider"] });
  const { data: rawMetrics } = useServiceProviderMetrics(
    isNewMetricsEnabled ? currentUser?.serviceProvider?.id : undefined
  );

  const handleOnClick = () => {
    navigate(["SUPERTALENT_OBJECTIVES"]);
  };

  const superTalentBadge = () => (
    <Icon
      as={WhiteSuperTalentBadge}
      boxSize={{ base: 4, lg: 6 }}
      marginRight="2px"
      marginBottom="5px"
      display="inline"
    />
  );

  if (!isNewMetricsEnabled) {
    if (context === "selection_rate") return null;
    return (
      <Card
        shadow={null}
        paddingX={["4", "4", "4"]}
        paddingY={["4", "4", "4"]}
        background={`linear-gradient(96.51deg, ${pink500} 0%, ${orange500} 99.87%);`}
        _hover={{
          background: `linear-gradient(96.51deg, ${pink600} 0%, ${orange600} 99.87%);`,
        }}
        w="full"
        h="fit-content"
        cursor="pointer"
        mt={{ base: 5, md: 8 }}
      >
        <Heading
          as="h4"
          textAlign="left"
          color="white"
          mb={3}
          fontWeight={700}
          fontSize={{ base: "xl", lg: "3xl" }}
        >
          <FormattedMessage
            {...messages.superTalentGoal}
            values={{ superTalentBadge }}
          />
        </Heading>
        <TrackingCard type={context as ITrackingTypes} onClick={handleOnClick} />
      </Card>
    );
  }

  // New design: gradient header + the exact same card components used in SuperTalentMetrics
  const metrics = (rawMetrics ?? []) as IExtendedMetric[];
  const findMetric = (name: string) => metrics.find((m) => m.name === name);

  const renderCard = () => {
    if (context === "repeat_rate") {
      return (
        <LoyalClienteleCard
          metric={findMetric("repeat_rate")}
          onClick={handleOnClick}
        />
      );
    }
    if (
      context === "realisation_rate" ||
      context === "realisation_rate_blocked"
    ) {
      return (
        <PromiseKeeperCard
          metric={findMetric("realisation_rate")}
          onClick={handleOnClick}
        />
      );
    }
    return (
      <TopPickCard
        metric={findMetric("selection_rate")}
        onClick={handleOnClick}
      />
    );
  };

  return (
    <Box w="full" mt={{ base: 5, md: 8 }} alignSelf="flex-start">
      {/* Gradient header — overlaps the card below by 16px */}
      <Box
        bg={`linear-gradient(96.51deg, ${pink500} 0%, ${orange500} 99.87%)`}
        px={4}
        pt={3}
        pb={`calc(0.75rem + 16px)`}
        borderTopRadius="lg"
        mb="-16px"
      >
        <Heading
          as="h4"
          color="white"
          fontWeight={700}
          fontSize={{ base: "xl", lg: "3xl" }}
        >
          <FormattedMessage
            {...messages.superTalentGoal}
            values={{ superTalentBadge }}
          />
        </Heading>
      </Box>

      {/* The exact same card component used in SuperTalentMetrics, with its own border radius */}
      {renderCard()}
    </Box>
  );
};
